import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { z } from "zod";

const filterOptionsSchema = z.object({
    data: z.object({
        attorneys: z.array(z.string()),
        caseManagers: z.array(z.string()),
        teams: z.array(z.string()),
        hasNullAttorney: z.boolean(),
        hasNullCaseManager: z.boolean(),
        hasNullTeam: z.boolean(),
    }),
});

export type LastTouchFilterOptions = z.infer<typeof filterOptionsSchema>["data"];

type Props = {
    reportType: "attorney" | "cm" | "all" | "";
    enabled: boolean;
};

export const useFetchLastTouchFilterOptions = ({
    reportType,
    enabled,
}: Props): UseQueryResult<LastTouchFilterOptions> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["last-touch-filter-options", reportType],
        enabled: enabled && reportType !== "",
        queryFn: async () => {
            const response = await fetch(apiUrl("/report/last-touch/filter-options"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reportType }),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch filter options");
            }

            const data = await response.json();
            return filterOptionsSchema.parse(data).data;
        },
    });
};
